import _ from "lodash";
import { inflate } from "pako";

const parse = (bin) => inflate(bin, { to: "string" });

import { IPS_RIDER, OPTIONS, OPTION_VALUES, PAYMENT_OPTIONS } from "@ipru-common/constants/InputValues";
import {
  COMMON_ASSETS_PATH,
  LMS_CONSTANTS,
  LifeCoverOption,
  // PERSONA_LIST,
  TAXES,
  quoteConstant,
} from "@ipru-common/constants/SiteConstants";
import { dropdownPremiumNumberList, readQueryParams, tracerAssociationId } from "./CommonFunctions";
import { PRODUCT_CODES } from "@ipru-common/constants/products";
import { TRACER_SOURCE_ID } from "@ipru-common/services/service";
import { t } from "i18next";

export const defaultPremium = {
  band: 0,
  band1: 0,
  bandSa: 0,
  band1Sa: 0,
  bandPremium: 0,
  band1Premium: 0,
};

export const fetchJSONFromGZFile = async (filename = "rates", assetsPath = "", version = "20250610") => {
  try {
    const response = await fetch(`${assetsPath || COMMON_ASSETS_PATH}/${filename}.json.gz?v=${version}`);
    const blob = await response?.blob();
    const res = await readSingleFile(blob);
    const jsonData = JSON.parse(parse(res));
    console.log("version", jsonData?.version);
    return jsonData;
  } catch (error) {
    console.error("Error fetching JSON:", error);
    throw error; // Rethrow the error for handling elsewhere
  }
};

export const fetchJSONFile = async (filename = "rates", assetsPath = "", version = "20250610") => {
  try {
    const response = await fetch(`${assetsPath || COMMON_ASSETS_PATH}/${filename}.json?v=${version}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const jsonData = await response.json();
    console.log("version", jsonData?.version);
    return jsonData;
  } catch (error) {
    console.error("Error fetching JSON:", error);
    throw error; // Rethrow the error for handling elsewhere
  }
};

export async function readSingleFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      resolve(e?.target?.result);
    };
    reader.readAsArrayBuffer(file);
    reader.onabort = reject;
    reader.onerror = reject;
  });
}

// Find Life cover, CI and ADB
export const findCoverage = ({ coverage, salary, age }) => {
  let found = false;

  // Iterate over each salary range
  for (const range in coverage) {
    const [minSalary, maxSalary] = range.split("_").map(Number);

    // Check if the salary falls within the range
    if (salary >= minSalary && salary < maxSalary) {
      // Iterate over each age group in the range
      for (const ageGroup in coverage[range]) {
        const ageGroupSA = coverage[range][ageGroup];
        // Check if the age falls within the age group
        if (age >= parseInt(ageGroup)) {
          found = ageGroupSA;
        }
      }
    }

    if (found) break;
  }

  return found;
};

export const findIPSDiversionScheme = ({ coverage, salary, age, education }) => {
  let found = false;

  //Iterate over each salary range
  for (const range in coverage) {
    const [minSalary, maxSalary] = range.split("_").map(Number);

    //Check if the salary falls within the range
    if (salary >= minSalary && salary < maxSalary) {
      //Iterate over each education code
      const eduGroup = coverage[range][education];

      //Check if scheme is distributed across ages
      if (eduGroup?.age) {
        for (const ageGroup in eduGroup.age) {
          if (age >= parseInt(ageGroup)) {
            found = eduGroup.age[ageGroup]?.scheme;
          }
        }
      } else {
        found = eduGroup?.scheme;
      }
    }
  }

  return found;
};

const { RP, LP5, LP7, LP10, LP15, SP, LP60, WLLP10, WLLP60, WLRP } = OPTION_VALUES;

const limitedPayConstOne = [{ ppt: 5, key: LP5 }];
const limitedPayConstTwo = [...limitedPayConstOne, { ppt: 7, key: LP7 }];
const limitedPayConstThree = [...limitedPayConstTwo, { ppt: 10, key: LP10 }];
const limitedPayConstFour = [...limitedPayConstThree, { ppt: 15, key: LP15 }];

const arrangeByOrder = (data, order) => {
  const orderMap = new Map(order.map((key, index) => [key.replace(/^WL/, ""), index]));

  return data.sort((a, b) => {
    // Remove "WL" prefix from the keys of a and b
    const aKey = a.key.replace(/^WL/, "");
    const bKey = b.key.replace(/^WL/, "");

    const aIndex = orderMap.get(aKey) !== undefined ? orderMap.get(aKey) : Infinity;
    const bIndex = orderMap.get(bKey) !== undefined ? orderMap.get(bKey) : Infinity;

    return aIndex - bIndex;
  });
};

const rearrangeNewPptSequence = (options, income, age, isHouseWife) => {
  if (options) {
    switch (true) {
      case (income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[400000].min &&
        income <= OPTION_VALUES.ANNUAL_INCOME.RANGE[400000].max) ||
        isHouseWife:
        {
          switch (true) {
            case age <= 39:
              options = arrangeByOrder(options, ["LP60", "RP", "LP15", "LP10", "LP7", "LP5", "SP"]);
              break;
            case age >= 40:
              options = arrangeByOrder(options, ["RP", "LP60", "LP15", "LP10", "LP7", "LP5", "SP"]);
              break;
          }
        }
        break;

      case income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[650000].min &&
        income <= OPTION_VALUES.ANNUAL_INCOME.RANGE[650000].max:
        {
          switch (true) {
            case age < 25:
              options = arrangeByOrder(options, ["LP60", "RP", "LP15", "LP10", "LP7", "LP5", "SP"]);
              break;
            case age >= 25 && age <= 34:
              options = arrangeByOrder(options, ["LP15", "RP", "LP60", "LP10", "LP7", "LP5", "SP"]);
              break;
            case age >= 35:
              options = arrangeByOrder(options, ["LP60", "RP", "LP15", "LP10", "LP7", "LP5", "SP"]);
              break;
          }
        }
        break;

      case income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[900000].min &&
        income <= OPTION_VALUES.ANNUAL_INCOME.RANGE[900000].max:
        {
          switch (true) {
            case age < 25:
              options = arrangeByOrder(options, ["LP60", "RP", "LP15", "LP10", "LP7", "LP5", "SP"]);
              break;
            case age >= 25 && age <= 44:
              options = arrangeByOrder(options, ["LP15", "RP", "LP60", "LP10", "LP7", "LP5", "SP"]);
              break;
            case age >= 45:
              options = arrangeByOrder(options, ["LP60", "RP", "LP15", "LP10", "LP7", "LP5", "SP"]);
              break;
          }
        }
        break;

      case income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[1500000].min &&
        income <= OPTION_VALUES.ANNUAL_INCOME.RANGE[1500000].max:
        {
          switch (true) {
            case age <= 39:
              options = arrangeByOrder(options, ["LP10", "RP", "LP60", "LP15", "LP5", "LP7", "SP"]);
              break;
            case age >= 40 && age <= 44:
              options = arrangeByOrder(options, ["LP15", "RP", "LP60", "LP10", "LP5", "LP7", "SP"]);
              break;
            case age >= 45:
              options = arrangeByOrder(options, ["LP60", "RP", "LP10", "LP15", "LP5", "LP7", "SP"]);
              break;
          }
        }
        break;

      case income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[2000000].min:
        {
          switch (true) {
            case age <= 44:
              options = arrangeByOrder(options, ["LP10", "RP", "LP60", "LP15", "LP5", "LP7", "SP"]);
              break;
            case age >= 45:
              options = arrangeByOrder(options, ["LP10", "RP", "LP60", "LP15", "LP5", "LP7", "SP"]);
              break;
          }
        }
        break;

      default:
        break;
    }
  }
};

// eslint-disable-next-line no-unused-vars
const rearrangePptSequence = (options, income, age, isHouseWife) => {
  if (options) {
    switch (true) {
      case (income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[400000].min &&
        income <= OPTION_VALUES.ANNUAL_INCOME.RANGE[400000].max) ||
        isHouseWife:
        {
          switch (true) {
            case age < 25:
              options = arrangeByOrder(options, ["LP60", "RP", "LP10", "LPT5", "LP15", "LP5", "LP7", "SP"]);
              break;
            case age >= 25 && age <= 34:
              options = arrangeByOrder(options, ["LP10", "RP", "LP60", "LPT5", "LP15", "LP5", "LP7", "SP"]);
              break;
            case age >= 35 && age <= 39:
              options = arrangeByOrder(options, ["LP60", "RP", "LP10", "LPT5", "LP15", "LP5", "LP7", "SP"]);
              break;
            case age >= 40:
              options = arrangeByOrder(options, ["RP", "LP60", "LP10", "LPT5", "LP15", "LP5", "LP7", "SP"]);
              break;
          }
        }

        break;
      case income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[650000].min &&
        income <= OPTION_VALUES.ANNUAL_INCOME.RANGE[900000].max:
        {
          switch (true) {
            case age < 25:
              options = arrangeByOrder(options, ["LP60", "RP", "LP10", "LPT5", "LP5", "LP15", "LP7", "SP"]);
              break;
            case age >= 25 && age <= 34:
              options = arrangeByOrder(options, ["LP10", "RP", "LP60", "LPT5", "LP5", "LP15", "LP7", "SP"]);
              break;
            case age >= 35:
              options = arrangeByOrder(options, ["LP60", "RP", "LP10", "LPT5", "LP5", "LP15", "LP7", "SP"]);
              break;
          }
        }
        break;

      case income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[1500000].min &&
        income <= OPTION_VALUES.ANNUAL_INCOME.RANGE[1500000].max:
        {
          switch (true) {
            case age < 25:
              options = arrangeByOrder(options, ["LP60", "RP", "LP10", "LPT5", "LP5", "LP15", "LP7", "SP"]);
              break;
            case age >= 25 && age <= 44:
              options = arrangeByOrder(options, ["LP10", "RP", "LP60", "LP5", "LP7", "LPT5", "LP15", "SP"]);
              break;
            case age >= 45:
              options = arrangeByOrder(options, ["LP60", "RP", "LP10", "LPT5", "LP15", "LP5", "LP7", "SP"]);

              break;
          }
        }
        break;

      case income >= OPTION_VALUES.ANNUAL_INCOME.RANGE[2000000].min:
        {
          switch (true) {
            case age < 25:
              options = arrangeByOrder(options, ["LP10", "RP", "LP60", "LP5", "LP7", "LPT5", "LP15", "SP"]);
              break;
            case age >= 25 && age <= 39:
              options = arrangeByOrder(options, ["LP5", "RP", "LP10", "LP60", "LP7", "LPT5", "LP15", "SP"]);
              break;
            case age >= 40:
              options = arrangeByOrder(options, ["LP10", "RP", "LP60", "LP5", "LP7", "LPT5", "LP15", "SP"]);
              break;
          }
        }
        break;

      default:
        break;
    }
  }
};

// Generate Premium payment options eg: Regular pay, Single Pay, Limited Pay
export const generateIpsPaymentOptions = ({ age, term, ci, isNegativePincode, income, isHouseWife, lumpSumAmt }) => {
  const { maxAge, maxAgeForLimitedPay, minTermForLp, wholeLifeAge, LP5_MAX_SA, singlePayMaxYears } = quoteConstant;

  const forWholeLife = term == wholeLifeAge - age;

  let options = [];
  if (term >= minTermForLp) {
    if (!forWholeLife) {
      switch (true) {
        case term === 10:
          options = [...limitedPayConstTwo];
          break;
        case term > 10 && term < 15:
          options = [...limitedPayConstTwo];
          break;
        case term >= 15 && term < 20:
          options = [...limitedPayConstThree];
          break;
        case term >= 20:
          options = [...limitedPayConstFour];
          break;
      }
    } else {
      options = [{ ppt: 10, key: WLLP10 }];
    }

    if (lumpSumAmt > LP5_MAX_SA) {
      // remove here lp 5 if lumpsum is more then 25 cr
      options = options.filter((ppo) => ppo.key !== LP5);
    }

    // Condition to create an Single Pay option
    // If critical illness is not selected and (75-age) > 20
    if (
      !ci &&
      !forWholeLife &&
      isNegativePincode === OPTION_VALUES.NO &&
      term >= minTermForLp &&
      term <= singlePayMaxYears
    ) {
      options = [...options, { ppt: 1, key: SP }];
    }
  }

  if (isNegativePincode === OPTION_VALUES.NO) {
    options = [
      {
        ppt: parseInt(term),
        key: forWholeLife ? WLRP : RP,
      },
      ...options,
    ];
  }

  // Condition to create an Limited Pay option
  // If maxAge (60 - age +5) is less than term and age > = minPolicyAge(18) and age < maxAgeForLimitedPay (55)
  if (term >= maxAge - age + minTermForLp && age <= maxAgeForLimitedPay) {
    const newPpt = maxAge - parseInt(age);

    // Check if ppt is already in options
    const isPptPresent = options.some((option) => option.ppt === newPpt);
    if (!isPptPresent) {
      options = [
        {
          ppt: newPpt,
          key: forWholeLife ? WLLP60 : LP60,
        },
        ...options,
      ];
    }
  }

  // if (!forWholeLife && term > 10) {
  //   const newPpt = term - 5;
  //   // Check if ppt is already in options
  //   const isPptPresent = options.some((option) => option.ppt === newPpt);
  //   if (!isPptPresent) {
  //     options = [{ ppt: newPpt, key: LPT5 }, ...options];
  //   }
  // }
  // const query = readQueryParams();
  // const persona = query.get("personaa");
  // if (
  //   !persona ||
  //   (persona && [PERSONA_LIST.MONTHLYINCOME, PERSONA_LIST.ALLINONE, PERSONA_LIST.WORKINGWOMEN].includes(persona))
  // )
  rearrangeNewPptSequence(options, income, age, isHouseWife);

  // Check if the first element's key is RP and swap if necessary
  if (
    options?.length > 1 &&
    options[0].key === RP &&
    !((income < OPTION_VALUES.ANNUAL_INCOME.RANGE[400000].max || isHouseWife) && age >= 40)
  ) {
    [options[0], options[1]] = [options[1], options[0]];
  }

  function setRecommendedPriority(data) {
    const priorityMapping = {
      RP: 1,
      LP10: 2,
      LP15: 2,
      LP60: 3,
      LPT5: 4,
      LP5: 5,
      LP7: 5,
    };

    return data.map((item) => ({
      ...item,
      recommendedPriority: priorityMapping[item.key] || null,
    }));
  }

  return setRecommendedPriority(options);
};

export const getPaymentFrequency = (tenure) => {
  const words = {
    [OPTION_VALUES.PAYMENT_TENURE.MONTHLY]: "month",
    [OPTION_VALUES.PAYMENT_TENURE.HALF_YEARLY]: "halfYear",
    [OPTION_VALUES.PAYMENT_TENURE.YEARLY]: "year",
  };

  return t(`quote.${words[tenure]}`);
};

//Common function to get sa bands and premium
export const findSaBandsAndPremium = ({
  SA,
  TERM,
  AGE,
  rates,
  maxTerm,
  SA_BANDS,
  tobacco = OPTION_VALUES.TOBACCO_CONSUMER_NO,
}) => {
  const result = { ...defaultPremium };

  for (const band in SA_BANDS) {
    if (parseInt(SA) >= SA_BANDS[band]) {
      result.band = band;
      result.band1 = result.band;

      const nextKey = parseInt(band) + 1;
      if (SA_BANDS?.[nextKey]) {
        result.band1 = nextKey;
      }

      result.bandSa = SA_BANDS[result.band];
      result.band1Sa = SA_BANDS[result.band1];
    }
  }
  const key1 = `${result.band}_${AGE}`;
  const key2 = `${result.band1}_${AGE}`;

  const newRates = fillGapInRates({
    data: rates,
    maxTerm,
    keys: [key1, key2],
  });

  const tobaccoLoading = tobacco === OPTION_VALUES.TOBACCO_CONSUMER_YES ? 1.7 : 1;

  if (newRates?.[key1]?.[TERM]) {
    result.bandPremium = Math.round(newRates[key1][TERM] * tobaccoLoading);
  }

  if (newRates?.[key2]?.[TERM]) {
    result.band1Premium = Math.round(newRates[key2][TERM] * tobaccoLoading);
  }

  return result;
};

const fillGapInRates = ({ data, maxTerm, keys = [] }) => {
  let result = data;
  keys.forEach((item) => {
    const remaining = maxTerm - result[item]?.length;

    for (let index = 0; index <= remaining; index++) {
      result[item] = [0, ...result[item]];
    }
  });

  return result;
};

export const getDefaultMaxTerm = (age) => {
  const userAge = parseInt(age);
  if (userAge <= 40) {
    return 60 - userAge;
  } else if (userAge <= 50) {
    return 65 - userAge;
  } else {
    return 75 - userAge;
  }
};

export const createTermDropdownOptions = (data, singlePaySelected = false) => {
  const { maxPolicyTerm, minPolicyTerm, wholeLifeAge } = quoteConstant;
  // const ppt1 = [OPTION_VALUES.RP, OPTION_VALUES.WLRP, OPTION_VALUES.WLLP60, OPTION_VALUES.LP60, OPTION_VALUES.LPT5];
  // const ppt2 = [OPTION_VALUES.LP5, OPTION_VALUES.LP7, OPTION_VALUES.WLLP10, OPTION_VALUES.LP10, OPTION_VALUES.LP15];
  const { age, isNegativePincode } = data;

  const isPremiumRefundValid = (age, coverTillAge) => {
    let isValid = false;
    let premiumValue = 100;
    // if (ppt1.includes(paymentOption)) {
    //   if (age >= 18 && age <= 50) {
    //     if (coverTillAge >= Math.max(age + 35, 65)) {
    //       isValid = true;
    //     }
    //   } else if (age >= 51 && age <= 60) {
    //     if (coverTillAge === 99) {
    //       isValid = true;
    //     }
    //   }
    // } else if (ppt2.includes(paymentOption)) {
    //   if (age >= 18 && age <= 45) {
    //     if (coverTillAge >= Math.max(age + 35, 65) && coverTillAge < Math.max(age + 40, 70)) {
    //       isValid = true;
    //     } else if (coverTillAge >= Math.max(age + 40, 70)) {
    //       isValid = true;
    //       premiumValue = 200;
    //     }
    //   } else if (age >= 46 && age <= 50) {
    //     if (coverTillAge >= Math.max(age + 35, 65) && coverTillAge < Math.max(age + 40, 70)) {
    //       isValid = true;
    //     } else if (coverTillAge === 99) {
    //       isValid = true;
    //       premiumValue = 200;
    //     }
    //   } else if (age >= 51 && age < 60) {
    //     if (coverTillAge === 99) {
    //       isValid = true;
    //       premiumValue = 200;
    //     }
    //   } else if (age === 60) {
    //     if (coverTillAge === 99) {
    //       isValid = true;
    //     }
    //   }
    // }
    if (age >= 18 && age <= 54) {
      if (coverTillAge >= Math.max(age + 31, 65)) {
        isValid = true;
      }
    } else if (age >= 55 && age <= 65) {
      if (coverTillAge === 99) {
        isValid = true;
      }
    }
    return { isValid, premiumValue };
  };
  const range = { start: 0, end: 0 };
  let options = [];
  let selectedDefault = 0;

  if (!singlePaySelected || isNegativePincode === OPTION_VALUES.YES) {
    selectedDefault = getDefaultMaxTerm(age);
    range.start = parseInt(age) + minPolicyTerm;
    range.end = maxPolicyTerm;
    if (isNegativePincode === OPTION_VALUES.NO) {
      const { isValid, premiumValue } = isPremiumRefundValid(age, wholeLifeAge);
      options = [
        {
          id: wholeLifeAge,
          name: `${wholeLifeAge} years`,
          premiumRefund: isValid,
          premiumValue,
        },
      ];
    }
  } else {
    const { spMaxAgeForTerm, singlePayMaxYears, minTermForLp } = quoteConstant;

    const term = spMaxAgeForTerm - parseInt(age);

    selectedDefault = term > singlePayMaxYears ? singlePayMaxYears : term;

    range.start = parseInt(age) + minTermForLp;
    range.end = parseInt(age) + singlePayMaxYears;
  }
  options = [
    ...dropdownPremiumNumberList({
      start: range.start,
      end: range.end,
      optionText: "years",
      age,
      isPremiumRefundValid,
    }),
    ...options,
  ];

  const entryIndex = options?.findIndex((item) => item.premiumRefund === true);
  const entryAge = entryIndex !== -1 ? options[entryIndex]?.id : null;

  const reversedIndex = [...options]?.reverse()?.findIndex((item) => item.premiumRefund === true);
  const upperIndex = reversedIndex !== -1 ? options?.length - 1 - reversedIndex : -1;
  const upperAge = upperIndex !== -1 ? options[upperIndex]?.id : null;

  return { options, selected: selectedDefault + parseInt(age), entryAge, upperAge };
};

export function getChannelType(Channel) {
  if (Channel === OPTION_VALUES.CR_NML) {
    return 1;
  } else if (Channel === OPTION_VALUES.PROPRIETARY || Channel === OPTION_VALUES.BOL) {
    return 2;
  } else {
    return 1;
  }
}

export const monthlyPremium = (val) => Math.round(quoteConstant.monthlyCharges * val * quoteConstant.monthlyCharges1);

export const halfYearlyPremium = (val) => Math.round((quoteConstant.halfYearlyCharges * val) / 2);

export const addTax = (value) => {
  return {
    withoutTax: value,
    firstYearPremium: includeTax(value),
    fromSecondYearOnwards: includeTax(value),
  };
};

export const includeTax = (value) => Math.round(value + (value * TAXES.GST_RATE) / 100);

export function calculateAdbCiFinalPremium(staff, platform, PPO, Channel, PPT, interpolatedValue) {
  const { ciNonSpYearly: NON_SP_MULTIPLY_BY, ciSpYearly: SP_MULTIPLY_BY } = quoteConstant;

  if (platform === "ON") {
    if (PPO !== OPTION_VALUES.SP) {
      return Math.round(interpolatedValue * NON_SP_MULTIPLY_BY);
    } else {
      return Math.round(interpolatedValue * SP_MULTIPLY_BY);
    }
  } else {
    if (Channel === OPTION_VALUES.BOL) {
      if (PPO === OPTION_VALUES.SP) {
        return Math.round(SP_MULTIPLY_BY * interpolatedValue);
      } else {
        return Math.round(NON_SP_MULTIPLY_BY * interpolatedValue);
      }
    } else {
      return Math.round(interpolatedValue);
    }
  }
}

export const getMinimumCover = () => ({
  SA: quoteConstant.minSumAssured,
  CI_SA: OPTIONS.CI_LIFE_COVER_OPTIONS[0],
  ADB_SA: quoteConstant.minSumAssured * 0.25,
  income_SA: quoteConstant.minSumAssured,
});

export const findSalaryRange = (value, ranges) => {
  for (const [key, { min, max }] of Object.entries(ranges)) {
    if (value >= min && value <= max) {
      return { key: parseInt(key), range: { min, max } };
    }
  }
  return null; // Return null if no range is found
};

export const prepareLeadData = ({ calculatorFormData, formFields, quoteId = null, spouseCover = false }) => {
  const {
    breakupDetail,
    CI_SA,
    paymentOption,
    payoutType,
    term,
    ppt,
    tenure,
    lumpSumAmt,
    monthlyIncomePayoutYears,
    monthlyIncomeAmt,
    lumpSumAndIncomeYears,
    increasingIncomePayoutYears,
    increasingIncomeAmt,
    lumpSumIncomeSingleInst,
    lumpSumIncomeRegularInst,
    planType,
  } = formFields;
  let payOutDetail = {
    LMS_premiumterm: term,
    Lumpsum: lumpSumAmt,
    Monthly_income: 0,
  };

  if (payoutType === OPTION_VALUES.INCOME) {
    payOutDetail.LMS_premiumterm = monthlyIncomePayoutYears;
    payOutDetail.Monthly_income = monthlyIncomeAmt;
  } else if (payoutType === OPTION_VALUES.INCREASING_INCOME) {
    payOutDetail.LMS_premiumterm = increasingIncomePayoutYears;
    payOutDetail.Monthly_income = increasingIncomeAmt;
  } else if (payoutType === OPTION_VALUES.LUMP_SUM_AND_INCOME) {
    payOutDetail.LMS_premiumterm = lumpSumAndIncomeYears;
    payOutDetail.Lumpsum = lumpSumIncomeSingleInst;
    payOutDetail.Monthly_income = lumpSumIncomeRegularInst;
  }

  const { ALL_IN_ONE, LIFE_HEALTH } = quoteConstant;

  const ciTerm = getCiTerm(paymentOption, calculatorFormData?.age, ppt, term);
  const urlParams = new URLSearchParams(window.location.search);
  const personaQueryParam = urlParams.get("personaa");
  const getPageName = (keyName) => {
    if (window.location.href.includes("buy-1-crore-term-insurance")) return `${keyName}-One Crore`;
    else if (personaQueryParam?.length > 0) {
      return `${keyName}-${personaQueryParam.replace(/_/g, " ")}`;
    } else return keyName || null;
  };

  let dob = "";
  if (calculatorFormData?.user_dob) {
    const [day, month, year] = calculatorFormData.user_dob.split("/");
    dob = `${month}/${day}/${year}`;
  }

  const getSpouseKeys = (quoteId, calculatorFormData) => {
    if (quoteId) {
      return {
        ...(calculatorFormData?.gender === OPTION_VALUES.GENDER.FEMALE ? { LMS_column_6: OPTION_VALUES.NO } : {}),
        LmsAdditionalDetails: {
          plantype: "spouse:opted",
        },
      };
    } else {
      if (spouseCover) {
        return {
          LmsAdditionalDetails: {
            plantype: "spouse:opted",
          },
        };
      }
    }
  };

  const data = {
    ...calculatorFormData,
    LMS_product_code:
      paymentOption === OPTION_VALUES.SP ? PRODUCT_CODES.iProtectSmartNewSinglePay : calculatorFormData?.productCode,
    LMS_page_name: getPageName(LMS_CONSTANTS.IPS_PAGE_NAME),
    LMS_pageName: getPageName(LMS_CONSTANTS.IPS_PAGE_NAME_2),
    LMS_premium: breakupDetail?.total,
    LMS_sumassured: lumpSumAmt,
    LMS_deathbenefit: lumpSumAmt,
    LMS_typeofplan: LMS_CONSTANTS[PAYMENT_OPTIONS[paymentOption]?.type] || "",
    LMS_payoutoption: LMS_CONSTANTS[payoutType],
    LMS_policyTerm: term,
    LMS_policypaymentfrequency: LMS_CONSTANTS[tenure],
    LMS_premiumterm: ppt,
    LMS_CIBenefit: [ALL_IN_ONE, LIFE_HEALTH].includes(planType) ? CI_SA : 0,
    ...getSpouseKeys(quoteId, calculatorFormData),

    Lumpsum: 0,
    Monthly_income: 0,
    Term_critical_illness: [ALL_IN_ONE, LIFE_HEALTH].includes(planType) ? ciTerm : 0,
    LMS_pageURL: LMS_CONSTANTS.IPS_PAGE_URL,
    dob,
  };

  return data;
};

export const getCiTerm = (paymentOption, age, ppt, term) =>
  [OPTION_VALUES.RP, OPTION_VALUES.WLRP].includes(paymentOption)
    ? _.sortBy([85 - parseInt(age), parseInt(term), 30])[0]
    : _.sortBy([parseInt(term), ppt])[0];

export const prepareTracerData = async ({
  lumpSumAmt,
  user_dob,
  gender,
  mobileno,
  emailid,
  premium,
  message,
  productCode,
}) => {
  const { associationId, associationIdIp, ip } = await tracerAssociationId({
    dob: user_dob,
    mobile: mobileno,
  });

  const queryParams = readQueryParams();

  let tracerData1 = {
    message: "comition",
    properties: {
      amount: lumpSumAmt,
      gender: gender,
      cardType: "ICICI BANK",
      mobileNo: mobileno,
      emailID: emailid,
      productCode: productCode || "UW3",
      vendorUid: queryParams.get("UID") || "480",
      associationId,
      premium,
      message:
        message || "www.icicilife.com/testing/new_signature/SignatureNewUI/icici-pru-signature-ulip-calculator.html",
      deviceId: "19 Jun 1989",
      ipAddress: ip,
    },
    source: TRACER_SOURCE_ID,
  };

  const tracerData2 = { ...tracerData1, properties: { ...tracerData1.properties, associationId: associationIdIp } };

  return { tracerData1, tracerData2 };
};

export const calculatePayoutDetails = (
  payoutType,
  monthlyIncomePayoutYears,
  SA,
  monthlyIncomeAmt,
  lumpSumAndIncomeYears,
  lumpSumIncomeSingleInst,
  income_duration = 0
) => {
  let payoutTerm = income_duration;
  let lumpsumPercentage = 0;
  let deathBenifit = SA;

  if (payoutType === OPTION_VALUES.LUMP_SUM) {
    payoutTerm = 0;
  }

  // if (payoutType === OPTION_VALUES.INCOME) {
  //   payoutTerm = monthlyIncomePayoutYears;
  //   lumpsumPercentage = (parseInt(monthlyIncomeAmt) / parseInt(SA)) * 100;
  // }

  if (payoutType === OPTION_VALUES.LUMP_SUM_AND_INCOME) {
    payoutTerm = lumpSumAndIncomeYears;
    lumpsumPercentage = (parseInt(lumpSumIncomeSingleInst) / parseInt(SA)) * 100;
  }

  return { payoutTerm, deathBenifit, lumpsumPercentage: Math.round(lumpsumPercentage) };
};

export const getLifeCoverSection = (data) => {
  return data ? LifeCoverOption[quoteConstant.LIFE_PLUS] : LifeCoverOption[quoteConstant.LIFE];
};

export const getCiPpt = (data) => {
  return data <= 20 ? data : 20;
};

export const getIpsPlusRiders = (quoteData) => {
  const riderData = [
    {
      Name: IPS_RIDER.ADB,
      SA: quoteData?.adb_selected ? quoteData?.ADB_SA : 0,
      Term: quoteData?.term,
      RiderOption: "",
      Percentage: "0",
      RiderPPT: quoteData?.ppt,
    },
    {
      Name: IPS_RIDER.CI,
      SA: quoteData?.ci_selected ? quoteData?.CI_SA : 0,
      Term: quoteData?.ciTerm,
      RiderOption: quoteData?.ciMode === OPTION_VALUES.CI_MODES.CLASSIC.years ? IPS_RIDER.CICL : IPS_RIDER.CICM,
      Percentage: "0",
      RiderPPT: getCiPpt(quoteData?.ppt),
    },
  ];
  return riderData;
};
export const checkCompanionDiscountIsApplicable = (companionStartDate, companionEndDate) => {
  const startDate = new Date(companionStartDate);
  const endDate = new Date(companionEndDate);
  const currentDate = new Date();

  // Compare timestamps
  return currentDate >= startDate && currentDate <= endDate;
};

export const formatDob = (dobValue) => {
  if (!dobValue) return "";
  dobValue = dobValue.toString().trim();
  if (dobValue.includes("/") && dobValue.split("/")[0].length === 2) {
    return dobValue;
  }
  if (dobValue.includes("-")) {
    const [yyyy, mm, dd] = dobValue.split("-");
    return `${dd}/${mm}/${yyyy}`;
  }
  return dobValue;
};
